import { FlexBox } from '@airship-platform/jam-web/components';
import { CloseIcon } from '@airship-platform/jam-web/icons';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { ProposalQualityScore } from '../../../data-access/proposalQualityScore';
import ROUTE_PATHS from '../../../routes/paths';
import CircularScoreIndicator from './CircularScoreIndicator';
import ScoreFeatureItem from './ScoreFeatureItem';

interface ProposalIds {
  appointmentId: string;
  appointmentProposalId: string;
  appointmentProposalOptionId?: string;
}

interface FeatureConfig {
  label: string;
  getPath: (ids: ProposalIds) => string;
}

const FEATURE_CONFIG: Record<string, FeatureConfig> = {
  COVER_PHOTO: {
    label: 'Upload cover photo',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'edit-presentation',
      }),
  },
  PHOTOS_UPLOADED: {
    label: 'Add existing system photos',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'capture-media',
      }),
  },
  HEADLINE_SUBHEADLINE: {
    label: 'Customize headline & subheadline',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'edit-presentation',
      }),
  },
  OPTION_SUMMARY: {
    label: 'Add proposal option summary',
    getPath: (ids) =>
      ids.appointmentProposalOptionId
        ? ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
            appointmentId: ids.appointmentId,
            appointmentProposalId: ids.appointmentProposalId,
            appointmentProposalOptionId: ids.appointmentProposalOptionId,
            extraPath: 'summary',
          })
        : ROUTE_PATHS.APPOINTMENT_PATH({
            appointmentId: ids.appointmentId,
            appointmentProposalId: ids.appointmentProposalId,
            path: 'options',
          }),
  },
  MULTIPLE_OPTIONS: {
    label: 'Add multiple options (3+)',
    getPath: (ids) =>
      ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        extraPath: 'manage',
      }),
  },
  CUSTOMIZED_UPGRADES: {
    label: 'Verify upgrades',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'upgrades',
      }),
  },
  EXPERIENCE_PRIORITIES: {
    label: 'Add home experience survey',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'customer-experience',
      }),
  },
  PROPOSAL_SUMMARY: {
    label: 'Add proposal summary',
    getPath: (ids) =>
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: ids.appointmentId,
        appointmentProposalId: ids.appointmentProposalId,
        path: 'edit-presentation',
      }),
  },
};

const getScoreSubtitle = (score: number): string => {
  if (score >= 80) {
    return 'Your proposal is ready to impress!';
  }
  return 'Add more to your proposal to boost your close rate.';
};

interface ProposalScoreMenuContentProps {
  data: ProposalQualityScore;
  onClose: () => void;
}

const ProposalScoreMenuContent: React.FC<ProposalScoreMenuContentProps> = ({
  data,
  onClose,
}) => {
  const navigate = useNavigate();
  const { currentProposal } = useAppointmentProposals();
  const { currentOption } = useAppointmentProposalOptions();

  const proposalIds: ProposalIds = {
    appointmentId: currentProposal?.appointmentId ?? '',
    appointmentProposalId: currentProposal?.appointmentProposalId ?? '',
    appointmentProposalOptionId: currentOption?.appointmentProposalOptionId,
  };

  const handleItemClick = (path: string) => {
    onClose();
    navigate(path);
  };

  const incompleteItems = data.breakdown
    .filter((item) => !item.present)
    .sort((a, b) => b.maxPoints - a.maxPoints);
  const completedItems = data.breakdown.filter((item) => item.present);

  return (
    <Box sx={{ p: 2 }}>
      <FlexBox justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Proposal score
        </Typography>
        <IconButton size="small" onClick={onClose} edge="end">
          <CloseIcon width={16} height={16} />
        </IconButton>
      </FlexBox>
      <FlexBox alignItems="center" gap={2} mb={2}>
        <CircularScoreIndicator score={data.score} size={64} thickness={4} />
        <Typography variant="body2" color={'text.secondary'} sx={{ flex: 1 }}>
          {getScoreSubtitle(data.score)}
        </Typography>
      </FlexBox>
      {incompleteItems.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box>
            {incompleteItems.map((item) => {
              const config = FEATURE_CONFIG[item.feature];
              return (
                <ScoreFeatureItem
                  key={item.feature}
                  label={config?.label || item.feature}
                  points={item.maxPoints}
                  isCompleted={false}
                  onClick={() =>
                    config && handleItemClick(config.getPath(proposalIds))
                  }
                />
              );
            })}
          </Box>
        </>
      )}

      {completedItems.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, mt: 1 }}
          >
            Completed
          </Typography>
          <Box>
            {completedItems.map((item) => {
              const config = FEATURE_CONFIG[item.feature];
              return (
                <ScoreFeatureItem
                  key={item.feature}
                  label={config?.label || item.feature}
                  points={item.points}
                  isCompleted
                />
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProposalScoreMenuContent;
